import React from 'react';
import PropTypes from 'prop-types';
import { LuBell, LuBug, LuImage, LuInfo, LuMessagesSquare, LuPaperclip, LuPhone, LuPin, LuSearch, LuSend, LuVideo, LuX } from 'react-icons/lu';
import GlassIcon from '../../../../../components/ui/GlassIcon';
import { useTranslation } from '../../../../../i18n/LanguageContext';
import { abs, typed } from '../../engine/stage';
import { Abs, Avatar, Btn, CARD, Field, Note, SectionHead, TILE } from '../kit';

// ── Mesajlar ────────────────────────────────────────────────────────────
export const MESSAGES = (top) => ({
  gate: { x: 256, y: top, w: 1000, h: 210 },
  open: { x: 586, y: top + 150, w: 180, h: 40 },
  panel: { x: 256, y: top, w: 1000, h: 708 - top },
  search: { x: 268, y: top + 14, w: 276, h: 38 },
  person: (index) => ({ x: 264, y: top + 92 + index * 58, w: 284, h: 52 }),
  voice: { x: 1156, y: top + 14, w: 38, h: 38 },
  video: { x: 1202, y: top + 14, w: 38, h: 38 },
  calls: { x: 1150, y: top + 8, w: 96, h: 50 },
  emoji: { x: 572, y: 708 - 96, w: 320, h: 30 },
  attach: { x: 572, y: 708 - 56, w: 40, h: 40 },
  input: { x: 620, y: 708 - 56, w: 568, h: 40 },
  send: { x: 1196, y: 708 - 56, w: 44, h: 40 },
});

const PEOPLE = [
  { initials: 'MD', name: 'Mehmet Demir', gradient: 'tw-from-violet-400 tw-to-indigo-600', unread: 2, time: '10:42', preview: 'messagePreview' },
  { initials: 'ZA', name: 'Zeynep Aydın', gradient: 'tw-from-amber-400 tw-to-orange-500', time: 'yesterday', preview: 'messagePreview2' },
  { initials: 'BY', name: 'Burak Yılmaz', gradient: 'tw-from-sky-400 tw-to-blue-600', time: '8 Eki', preview: 'messagePreview3' },
];

/**
 * İletişim › Mesajlar: the gate card ("Mesajları Aç") and the messenger
 * (people on the left, the chat on the right).
 * state: { opened, search, chat, typedText, sent, file, pressed }
 */
export const MessagesPane = ({ top, state, p = 0 }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.messenger.${key}`, vars);
  const M = MESSAGES(top);
  if (!state.opened) {
    return (
      <Abs rect={M.gate} className={`tw-flex tw-flex-col tw-items-center tw-pt-6 tw-text-center ${CARD}`}>
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="md"><LuMessagesSquare aria-hidden="true" className="tw-h-6 tw-w-6" /></GlassIcon>
        <p className="tw-mt-3 tw-text-lg tw-font-semibold tw-text-slate-900 dark:tw-text-white">{t('profilePage.messages.title')}</p>
        <p className="tw-text-sm tw-text-slate-500">{t('profilePage.messages.description')}</p>
        <span className="tw-mt-2 tw-rounded-full tw-bg-rose-500 tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-bold tw-text-white">{t('profilePage.messages.unread', { count: 2 })}</span>
        <span style={{ position: 'absolute', left: M.open.x - M.gate.x, top: M.open.y - M.gate.y, width: M.open.w, height: M.open.h }} className={`tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-teal-500/30 ${state.pressed ? 'tw-scale-95' : ''}`}>
          <LuMessagesSquare aria-hidden="true" className="tw-h-4 tw-w-4" />{t('profilePage.messages.load')}
        </span>
      </Abs>
    );
  }
  const chat = state.chat ? PEOPLE[0] : null;
  const typedText = state.typedText ? typed(t('userGuide.stage.myMessage'), p, state.typeFrom || 0, state.typeTo || 0.4) : '';
  const bottom = 708;
  return (
    <>
      <Abs rect={M.panel} className={`tw-overflow-hidden ${CARD}`}>
        <span style={{ left: 300 }} className="tw-absolute tw-inset-y-0 tw-w-px tw-bg-slate-200/80 dark:tw-bg-white/10" />
      </Abs>
      <Field rect={M.search} value={state.search} placeholder={label('search')} prefix={<LuSearch aria-hidden="true" className="tw-h-4 tw-w-4" />} focused={state.searchFocus} />
      <span style={{ left: 272, top: top + 68 }} className="tw-absolute tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400">{label('conversations')}</span>
      {PEOPLE.filter((person) => !state.search || person.name.toLowerCase().includes(state.search.toLowerCase())).map((person, index) => (
        <Abs key={person.name} rect={M.person(index)} className={`tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-px-2 ${chat && index === 0 ? 'tw-bg-teal-500/10' : ''}`}>
          <Avatar initials={person.initials} size={38} gradient={person.gradient} />
          <span className="tw-min-w-0 tw-flex-1">
            <span className="tw-flex tw-justify-between tw-text-sm"><b className="tw-truncate tw-text-slate-900 dark:tw-text-white">{person.name}</b><span className="tw-text-[11px] tw-text-slate-400">{person.time === 'yesterday' ? label('yesterday') : person.time}</span></span>
            <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500">{t(`userGuide.stage.${person.preview}`)}</span>
          </span>
          {person.unread && !(chat && index === 0) ? <span className="tw-grid tw-h-5 tw-min-w-[1.25rem] tw-place-items-center tw-rounded-full tw-bg-rose-500 tw-px-1 tw-text-[10px] tw-font-bold tw-text-white">{person.unread}</span> : null}
        </Abs>
      ))}
      {!chat ? (
        <div style={{ left: 560, top: top + 160, width: 690 }} className="tw-absolute tw-text-center">
          <p className="tw-text-base tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{label('pickTitle')}</p>
          <p className="tw-mx-auto tw-mt-1 tw-max-w-md tw-text-sm tw-text-slate-500">{label('pickText')}</p>
        </div>
      ) : (
        <>
          <div style={{ left: 572, top: top + 12 }} className="tw-absolute tw-flex tw-items-center tw-gap-3">
            <Avatar initials={chat.initials} size={40} gradient={chat.gradient} />
            <span>
              <span className="tw-block tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{chat.name}</span>
              <span className="tw-block tw-text-xs tw-text-slate-500">mehmet.demir@habcarecro.com</span>
            </span>
          </div>
          <span style={abs(M.voice)} className="tw-absolute tw-grid tw-place-items-center tw-rounded-full tw-bg-emerald-500/10 tw-text-emerald-600"><LuPhone aria-hidden="true" className="tw-h-4 tw-w-4" /></span>
          <span style={abs(M.video)} className="tw-absolute tw-grid tw-place-items-center tw-rounded-full tw-bg-sky-500/10 tw-text-sky-600"><LuVideo aria-hidden="true" className="tw-h-4 tw-w-4" /></span>
          <span style={{ left: 566, top: top + 64, width: 684 }} className="tw-absolute tw-h-px tw-bg-slate-200/80 dark:tw-bg-white/10" />
          <span style={{ left: 870, top: top + 78 }} className="tw-absolute tw-rounded-full tw-bg-slate-900/[0.05] tw-px-3 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-slate-500 dark:tw-bg-white/10">{label('today')}</span>
          <div style={{ left: 584, top: top + 110, maxWidth: 420 }} className="tw-absolute tw-rounded-2xl tw-rounded-bl-md tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-py-2 tw-text-sm tw-text-slate-800 dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-text-slate-100">
            {t('userGuide.stage.messagePreview')}<span className="tw-ml-2 tw-text-[10px] tw-text-slate-400">10:42</span>
          </div>
          {state.sent && (
            <div style={{ right: 1280 - 1236, top: top + 166, maxWidth: 420 }} className="tw-absolute tw-rounded-2xl tw-rounded-br-md tw-bg-gradient-to-r tw-from-teal-500 tw-to-cyan-600 tw-px-3.5 tw-py-2 tw-text-sm tw-text-white">
              {t('userGuide.stage.myMessage')}<span className="tw-ml-2 tw-text-[10px] tw-text-white/70">10:45</span>
            </div>
          )}
          {state.fileSent && (
            <div style={{ right: 1280 - 1236, top: top + 216 }} className="tw-absolute tw-flex tw-items-center tw-gap-2 tw-rounded-2xl tw-rounded-br-md tw-bg-gradient-to-r tw-from-teal-500 tw-to-cyan-600 tw-p-2 tw-text-sm tw-text-white">
              <span className="tw-grid tw-h-16 tw-w-24 tw-place-items-center tw-rounded-xl tw-bg-white/20"><LuImage aria-hidden="true" className="tw-h-6 tw-w-6" /></span>
              <span className="tw-pr-2 tw-text-xs">vizit-notu.jpg</span>
            </div>
          )}
          {state.file && !state.fileSent && (
            <span style={{ left: 572, top: bottom - 132 }} className="tw-absolute tw-inline-flex tw-h-7 tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-teal-500/10 tw-px-3 tw-text-xs tw-font-semibold tw-text-teal-800 dark:tw-text-teal-200">
              <LuPaperclip aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />vizit-notu.jpg<LuX aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            </span>
          )}
          <Abs rect={M.emoji} className="tw-flex tw-items-center tw-gap-1.5 tw-text-lg">
            {['👋', '👍', '❤️', '😊', '😂', '🙏', '🔥', '👏'].map((emoji) => <span key={emoji} className={`tw-grid tw-h-7 tw-w-7 tw-place-items-center tw-rounded-lg ${TILE}`}>{emoji}</span>)}
          </Abs>
          <span style={abs(M.attach)} className={`tw-absolute tw-grid tw-place-items-center tw-rounded-full tw-text-slate-500 ${state.attachFocus ? 'tw-bg-teal-500/15 tw-text-teal-700' : 'tw-bg-slate-900/[0.05] dark:tw-bg-white/10'}`}><LuPaperclip aria-hidden="true" className="tw-h-4 tw-w-4" /></span>
          <Field rect={M.input} value={state.sent ? '' : typedText} placeholder={label('placeholder')} focused={state.inputFocus} />
          <span style={abs(M.send)} className={`tw-absolute tw-grid tw-place-items-center tw-rounded-full tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-text-white tw-shadow-md ${state.sendPressed ? 'tw-scale-90' : ''}`}><LuSend aria-hidden="true" className="tw-h-4 tw-w-4" /></span>
        </>
      )}
    </>
  );
};

MessagesPane.propTypes = { top: PropTypes.number.isRequired, state: PropTypes.object.isRequired, p: PropTypes.number };

// ── Bildirimler ─────────────────────────────────────────────────────────
export const ANNOUNCEMENTS = (top) => ({
  list: { x: 256, y: top + 64, w: 1000, h: 300 },
  item: (index) => ({ x: 256, y: top + 64 + index * 96, w: 1000, h: 84 }),
});

export const AnnouncementsPane = ({ top }) => {
  const { t } = useTranslation();
  const A = ANNOUNCEMENTS(top);
  return (
    <>
      <SectionHead top={top} icon={LuBell} gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" title={t('profilePage.notifications.title')} description={t('profilePage.notifications.description')} />
      {[['announcement1', '10.10.2026 09:30'], ['announcement2', '02.10.2026 14:05'], ['announcement3', '24.09.2026 11:20']].map(([key, date], index) => (
        <Abs key={key} rect={A.item(index)} className={`tw-flex tw-gap-3 tw-p-4 ${CARD}`}>
          <span className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-amber-500/15 tw-text-amber-600"><LuPin aria-hidden="true" className="tw-h-4 tw-w-4" /></span>
          <span className="tw-min-w-0">
            <span className="tw-block tw-text-sm tw-leading-snug tw-text-slate-800 dark:tw-text-slate-100">{t(`userGuide.stage.${key}`)}</span>
            <span className="tw-mt-1 tw-block tw-text-[11px] tw-text-slate-400">{date}</span>
          </span>
        </Abs>
      ))}
    </>
  );
};

AnnouncementsPane.propTypes = { top: PropTypes.number.isRequired };

// ── Hata Bildir ─────────────────────────────────────────────────────────
export const BUG = (top) => ({
  card: { x: 256, y: top + 60, w: 1000, h: 400 },
  tips: { x: 280, y: top + 84, w: 952, h: 44 },
  summary: { x: 280, y: top + 164, w: 952, h: 112 },
  screenshot: { x: 280, y: top + 316, w: 140, h: 36 },
  submit: { x: 1040, y: top + 394, w: 192, h: 42 },
});

export const BugReportPane = ({ top, state, p = 0 }) => {
  const { t } = useTranslation();
  const label = (key) => t(`profilePage.errorReport.${key}`);
  const B = BUG(top);
  const text = state.typed ? typed(t('userGuide.stage.bugText'), p, state.typeFrom || 0, state.typeTo || 0.6) : '';
  return (
    <>
      <SectionHead top={top} icon={LuBug} gradient="tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30" title={label('title')} description={label('description')} />
      <Abs rect={B.card} className={CARD} />
      <Note rect={B.tips} icon={LuInfo}>{label('tips')}</Note>
      <Field rect={B.summary} label={label('summary')} required area value={state.done ? t('userGuide.stage.bugText') : text} placeholder={label('summaryPlaceholder')} focused={state.focus === 'summary'} />
      <span style={{ left: 282, top: top + 294 }} className="tw-absolute tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('screenshot')} <span className="tw-font-normal tw-text-slate-400">· {label('screenshotHint')}</span></span>
      <Btn rect={B.screenshot} icon={LuImage} pressed={state.shotPressed}>{label('chooseScreenshot')}</Btn>
      {state.shot && (
        <span style={{ left: 432, top: top + 320 }} className="tw-absolute tw-inline-flex tw-h-7 tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-teal-500/10 tw-px-3 tw-text-xs tw-font-semibold tw-text-teal-800 dark:tw-text-teal-200">
          <LuImage aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />ekran-goruntusu.png<LuX aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
        </span>
      )}
      <Btn rect={B.submit} variant="primary" icon={LuSend} pressed={state.pressed}>{label('submit')}</Btn>
    </>
  );
};

BugReportPane.propTypes = { top: PropTypes.number.isRequired, state: PropTypes.object.isRequired, p: PropTypes.number };
